import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, PlusIcon, PlayIcon, TrendingUpIcon, ZapIcon, TargetIcon, UsersIcon, CheckCircleIcon, ClockIcon, SparklesIcon, BookOpenIcon, BarChart3Icon } from 'lucide-react';
import { mockPlaybooksNew } from '../mockData';
export function PlaybookDetailPage() {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'overview' | 'plays' | 'measurement'>('overview');
  const playbook = mockPlaybooksNew.find(p => p.id === id);
  if (!playbook) {
    return <div className="w-full h-screen flex items-center justify-center bg-gray-50" data-id="element-5589">
        <div className="text-center" data-id="element-5590">
          <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-5591">
            Playbook Not Found
          </h2>
          <p className="text-gray-600 mb-4" data-id="element-5592">
            The playbook you're looking for doesn't exist.
          </p>
          <button onClick={() => navigate('/playbooks')} className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors" data-id="element-5593">
            Back to Playbooks
          </button>
        </div>
      </div>;
  }
  const getLifecycleColor = (lifecycle: string) => {
    switch (lifecycle) {
      case 'Acquire':
        return 'bg-green-500';
      case 'Close':
        return 'bg-blue-500';
      case 'Expand':
        return 'bg-purple-500';
      default:
        return 'bg-gray-500';
    }
  };
  return <div className="w-full min-h-screen bg-gray-50" data-id="element-5594">
      {/* Header */}
      <div className="bg-white border-b border-gray-200" data-id="element-5595">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6" data-id="element-5596">
          {/* Back Button */}
          <button onClick={() => navigate('/playbooks')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors" data-id="element-5597">
            <ArrowLeftIcon className="w-4 h-4" data-id="element-5598" />
            <span className="text-sm font-medium" data-id="element-5599">Back to Playbooks</span>
          </button>
          {/* Title Section */}
          <div className="flex items-start gap-6 mb-6" data-id="element-5600">
            <div className="text-6xl" data-id="element-5601">{playbook.icon}</div>
            <div className="flex-1" data-id="element-5602">
              <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-5603">
                {playbook.title}
              </h1>
              <p className="text-lg text-gray-600 mb-4" data-id="element-5604">{playbook.summary}</p>
              {/* Tags */}
              <div className="flex flex-wrap gap-2 mb-4" data-id="element-5605">
                {playbook.lifecycle.map(lifecycle => <span key={lifecycle} className={`px-3 py-1 text-white text-sm font-medium rounded-full ${getLifecycleColor(lifecycle)}`} data-id="element-5606">
                    {lifecycle}
                  </span>)}
                {playbook.motion.map(motion => <span key={motion} className="px-3 py-1 bg-purple-100 text-purple-700 text-sm font-medium rounded-lg" data-id="element-5607">
                    {motion}
                  </span>)}
                {playbook.ai_ready && <span className="px-3 py-1 bg-blue-100 text-blue-700 text-sm font-medium rounded-lg flex items-center gap-1" data-id="element-5608">
                    <SparklesIcon className="w-4 h-4" data-id="element-5609" />
                    AI-Ready
                  </span>}
              </div>
              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3" data-id="element-5610">
                <button onClick={() => navigate('/programs/overview')} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium flex items-center gap-2" data-id="element-5611">
                  <PlusIcon className="w-4 h-4" data-id="element-5612" />
                  Create Program
                </button>
                <button onClick={() => navigate('/gtm-studio/workspace')} className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium flex items-center gap-2" data-id="element-5613">
                  <PlayIcon className="w-4 h-4" data-id="element-5614" />
                  Launch in Studio
                </button>
              </div>
            </div>
          </div>
          {/* Tabs */}
          <div className="flex gap-6 border-b border-gray-200" data-id="element-5615">
            {[{
            id: 'overview',
            label: 'Overview',
            icon: BookOpenIcon
          }, {
            id: 'plays',
            label: 'Plays',
            icon: PlayIcon
          }, {
            id: 'measurement',
            label: 'Measurement',
            icon: BarChart3Icon
          }].map(tab => {
            const Icon = tab.icon;
            return <button key={tab.id} onClick={() => setActiveTab(tab.id as any)} className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${activeTab === tab.id ? 'border-red-600 text-red-600' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-5616">
                  <Icon className="w-4 h-4" data-id="element-5617" />
                  {tab.label}
                </button>;
          })}
          </div>
        </div>
      </div>
      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8" data-id="element-5618">
        {activeTab === 'overview' && <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-5619">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6" data-id="element-5620">
              {/* Key Metrics */}
              <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-5621">
                <h2 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-5622">
                  Expected Impact
                </h2>
                <div className="grid grid-cols-2 gap-4" data-id="element-5623">
                  <div className="p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-lg border border-green-200" data-id="element-5624">
                    <div className="flex items-center gap-2 mb-2" data-id="element-5625">
                      <TrendingUpIcon className="w-5 h-5 text-green-600" data-id="element-5626" />
                      <p className="text-sm font-medium text-green-900" data-id="element-5627">
                        Expected Lift
                      </p>
                    </div>
                    <p className="text-3xl font-bold text-green-900" data-id="element-5628">
                      {playbook.expected_lift}
                    </p>
                  </div>
                  <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border border-blue-200" data-id="element-5629">
                    <div className="flex items-center gap-2 mb-2" data-id="element-5630">
                      <ClockIcon className="w-5 h-5 text-blue-600" data-id="element-5631" />
                      <p className="text-sm font-medium text-blue-900" data-id="element-5632">
                        Time to Value
                      </p>
                    </div>
                    <p className="text-3xl font-bold text-blue-900" data-id="element-5633">
                      {playbook.time_to_value}
                    </p>
                  </div>
                </div>
              </div>
              {/* Triggers */}
              <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-5634">
                <h2 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-5635">
                  Activation Triggers
                </h2>
                <div className="space-y-2" data-id="element-5636">
                  {playbook.triggers.map((trigger, index) => <div key={index} className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200" data-id="element-5637">
                      <ZapIcon className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" data-id="element-5638" />
                      <span className="text-sm text-blue-900" data-id="element-5639">{trigger}</span>
                    </div>)}
                </div>
              </div>
              {/* Target Personas */}
              <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-5640">
                <h2 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-5641">
                  Target Personas
                </h2>
                <div className="flex flex-wrap gap-2" data-id="element-5642">
                  {playbook.personas.map(persona => <span key={persona} className="px-4 py-2 bg-purple-50 text-purple-700 text-sm font-medium rounded-lg border border-purple-200" data-id="element-5643">
                      {persona}
                    </span>)}
                </div>
              </div>
              {/* Eligibility */}
              <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-5644">
                <h2 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-5645">
                  Eligibility Criteria
                </h2>
                <div className="space-y-4" data-id="element-5646">
                  <div data-id="element-5647">
                    <p className="text-sm font-medium text-gray-700 mb-2" data-id="element-5648">
                      Account Fit
                    </p>
                    <div className="space-y-1" data-id="element-5649">
                      {playbook.eligibility.account_fit.map((fit, index) => <div key={index} className="flex items-center gap-2" data-id="element-5650">
                          <CheckCircleIcon className="w-4 h-4 text-green-600" data-id="element-5651" />
                          <span className="text-sm text-gray-600" data-id="element-5652">{fit}</span>
                        </div>)}
                    </div>
                  </div>
                  <div data-id="element-5653">
                    <p className="text-sm font-medium text-gray-700 mb-2" data-id="element-5654">
                      Buying Group Roles
                    </p>
                    <div className="flex flex-wrap gap-2" data-id="element-5655">
                      {playbook.eligibility.buying_group_roles.map(role => <span key={role} className="px-3 py-1 bg-gray-100 text-gray-700 text-xs rounded-full" data-id="element-5656">
                          {role}
                        </span>)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Sidebar */}
            <div className="space-y-6" data-id="element-5657">
              {/* Owner */}
              <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-5658">
                <h2 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-5659">
                  Owner
                </h2>
                <div className="flex items-center gap-3" data-id="element-5660">
                  <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center" data-id="element-5661">
                    <span className="text-red-600 font-semibold" data-id="element-5662">
                      {playbook.owner.name.charAt(0)}
                    </span>
                  </div>
                  <div data-id="element-5663">
                    <p className="text-sm font-medium text-gray-900" data-id="element-5664">
                      {playbook.owner.name}
                    </p>
                    <p className="text-xs text-gray-600" data-id="element-5665">
                      {playbook.owner.team}
                    </p>
                  </div>
                </div>
              </div>
              {/* Data Needed */}
              <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-5666">
                <h2 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-5667">
                  Data Requirements
                </h2>
                <div className="space-y-2" data-id="element-5668">
                  {playbook.data_needed.map(data => <div key={data} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg" data-id="element-5669">
                      <div className="w-2 h-2 bg-red-600 rounded-full" data-id="element-5670" />
                      <span className="text-sm text-gray-900" data-id="element-5671">{data}</span>
                    </div>)}
                </div>
              </div>
              {/* Complexity */}
              <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-5672">
                <h2 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-5673">
                  Implementation
                </h2>
                <div className="space-y-3" data-id="element-5674">
                  <div data-id="element-5675">
                    <p className="text-xs text-gray-600 mb-1" data-id="element-5676">Complexity</p>
                    <span className="px-3 py-1 bg-blue-100 text-blue-700 text-sm font-medium rounded-lg capitalize" data-id="element-5677">
                      {playbook.complexity}
                    </span>
                  </div>
                  <div data-id="element-5678">
                    <p className="text-xs text-gray-600 mb-1" data-id="element-5679">Duration</p>
                    <span className="px-3 py-1 bg-purple-100 text-purple-700 text-sm font-medium rounded-lg" data-id="element-5680">
                      {playbook.duration_days} days
                    </span>
                  </div>
                </div>
              </div>
              {/* Activation Rate */}
              {playbook.activation_rate !== undefined && <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg border border-green-200 p-6" data-id="element-5681">
                  <h2 className="text-lg font-semibold text-green-900 mb-2" data-id="element-5682">
                    Activation Rate (30d)
                  </h2>
                  <p className="text-4xl font-bold text-green-900 mb-3" data-id="element-5683">
                    {playbook.activation_rate}%
                  </p>
                  <div className="w-full bg-white rounded-full h-3" data-id="element-5684">
                    <div className="bg-green-600 h-3 rounded-full transition-all" style={{
                width: `${playbook.activation_rate}%`
              }} data-id="element-5685" />
                  </div>
                </div>}
            </div>
          </div>}
        {activeTab === 'plays' && <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-5686">
            <div className="flex items-center justify-between mb-6" data-id="element-5687">
              <h2 className="text-lg font-semibold text-gray-900" data-id="element-5688">
                Included Plays ({playbook.plays.length})
              </h2>
              <button onClick={() => navigate('/plays/library')} className="text-sm text-red-600 hover:text-red-700 font-medium" data-id="element-5689">
                View in Library →
              </button>
            </div>
            <div className="space-y-3" data-id="element-5690">
              {playbook.plays.map(playId => <div key={playId} className="p-4 bg-gray-50 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors" data-id="element-5691">
                  <div className="flex items-center justify-between" data-id="element-5692">
                    <div className="flex items-center gap-3" data-id="element-5693">
                      <PlayIcon className="w-5 h-5 text-gray-600" data-id="element-5694" />
                      <span className="text-sm font-medium text-gray-900" data-id="element-5695">
                        Play {playId}
                      </span>
                    </div>
                    <button onClick={() => navigate(`/plays/${playId}`)} className="text-sm text-red-600 hover:text-red-700 font-medium" data-id="element-5696">
                      View Details
                    </button>
                  </div>
                </div>)}
            </div>
          </div>}
        {activeTab === 'measurement' && <div className="space-y-6" data-id="element-5697">
            {/* Primary KPIs */}
            <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-5698">
              <h2 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-5699">
                Primary KPIs
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-5700">
                {playbook.kpis.map(kpi => <div key={kpi} className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border border-blue-200" data-id="element-5701">
                    <div className="flex items-center gap-2 mb-2" data-id="element-5702">
                      <TargetIcon className="w-5 h-5 text-blue-600" data-id="element-5703" />
                      <p className="text-sm font-medium text-blue-900" data-id="element-5704">{kpi}</p>
                    </div>
                    <p className="text-xs text-blue-700" data-id="element-5705">Track and optimize</p>
                  </div>)}
              </div>
            </div>
            {/* Assets */}
            <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-5706">
              <h2 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-5707">
                Required Assets
              </h2>
              <div className="space-y-3" data-id="element-5708">
                {playbook.assets.map((asset, index) => <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200" data-id="element-5709">
                    <div data-id="element-5710">
                      <p className="text-sm font-medium text-gray-900" data-id="element-5711">
                        {asset.name}
                      </p>
                      <p className="text-xs text-gray-600 capitalize" data-id="element-5712">
                        {asset.type.replace('_', ' ')}
                      </p>
                    </div>
                    <span className="text-xs text-gray-500" data-id="element-5713">{asset.ref}</span>
                  </div>)}
              </div>
            </div>
          </div>}
      </div>
    </div>;
}